import { ProductFeature } from "@app/shared/types";
import { alpha, AppBar, Box, Slide, Toolbar, Typography, useScrollTrigger } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { NAVBAR_HEIGHT, NAVBAR_HEIGHT_MOBILE } from "app/styles";
import classNames from "classnames";
import { selectIsLoggedIn, selectIsTeacher, selectUser } from "features/auth/auth";
import { useDetectScroll } from "hooks/useDetectScroll";
import { useQueryParams } from "hooks/useQueryParams";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, RouteProps, useLocation } from "react-router";
import { routes } from "../../app/routes";
import { selectCanAccessMemberZone } from "../auth/auth";
import { closeMenu, selectIsMenuOpen } from "./navigationSlice";
import { NavLink } from "./NavLink";
import { MENU_SHADOW_HEIGHT, TopBarMenu } from "./TopBarMenu";

const useStyles = makeStyles((theme) => ({
    appBar: {
        minHeight: NAVBAR_HEIGHT,
        overflow: "hidden",

        "&:before": {
            content: "''",
            position: "absolute",
            zIndex: -1,
            left: 0,
            bottom: MENU_SHADOW_HEIGHT,
            top: 0,
            right: 0,
            boxShadow: `${MENU_SHADOW_HEIGHT}px 0 ${MENU_SHADOW_HEIGHT}px ${alpha(
                theme.palette.grey900,
                0.1,
            )}`,
            transition: "opacity 500ms ease",
            opacity: 1,
        },

        [theme.breakpoints.down("lg")]: {
            minHeight: NAVBAR_HEIGHT_MOBILE,
        },
    },

    memberZoneAppBar: {
        "&:before": {
            opacity: 1,
        },
    },

    scrolledAppBar: {
        "&:before": {
            opacity: 1,
        },
    },
    scrolledPageFoldAppBar: {
        display: "none",
    },

    fullPageMenuOpen: {
        "&:before": {
            [theme.breakpoints.down("lg")]: {
                opacity: 0,
            },
        },
    },

    container: {
        padding: theme.spacing(0, 4),
        minHeight: NAVBAR_HEIGHT - MENU_SHADOW_HEIGHT,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        backgroundColor: theme.palette.background.default,

        [theme.breakpoints.down("lg")]: {
            minHeight: NAVBAR_HEIGHT_MOBILE - MENU_SHADOW_HEIGHT,
        },
    },

    toolbar: {
        marginTop: 0,
    },

    barSpace: {
        flexGrow: 1,
    },

    logo: {
        width: 60,
        height: 60,
        verticalAlign: "middle",

        // [theme.breakpoints.down("sm")]: {
        //     width: "none",
        // },
    },
}));

const getPagesWithoutTopBar = (isActiveMember: boolean) => [
    // Signup & setup flow
    routes.signupBilling(),
    routes.memberOnboarding(),
    routes.setupEditProfilePage(),
    routes.setupWelcomeSession(),
    routes.setupShowYouAround(),
    routes.setupAllInterestGroups(),
    routes.setupAddInterestGroup(":groupId"),
    routes.setupConfirmInterestGroup(":groupId"),

    // Other pages
    routes.cancelMembership(),
];

const PAGES_WITHOUT_BRAND_NAME = [{ path: routes.home(), exact: true }];

const PAGES_WITHOUT_INITIAL_APPBAR: Array<string | RouteProps> = [];

const getPagesWithoutMenu = (isActiveMember: boolean) => [
    routes.cancelMembership(),
    routes.login(),
    routes.signup(),
    routes.teacherPostSession(""),
];

const hideAppbarInitially = (path: string): boolean => {
    return _.some(PAGES_WITHOUT_INITIAL_APPBAR, (route) => matchPath(path, route));
};

const skipBrandName = (path: string): boolean => {
    return _.some(PAGES_WITHOUT_BRAND_NAME, (route) => matchPath(path, route));
};

const skipMenu = (path: string, isActiveMember: boolean): boolean => {
    return _.some(getPagesWithoutMenu(isActiveMember), (route) => matchPath(path, route));
};

const skipTopBar = (path: string, isActiveMember: boolean): boolean =>
    _.some(getPagesWithoutTopBar(isActiveMember), (route) => matchPath(path, route));

export const TopBar = (props: { includeMenu?: boolean }): JSX.Element | null => {
    const classes = useStyles();
    const hasScrolled = useDetectScroll();
    const hasScrolledPageFold = useDetectScroll({
        threshold: window.innerHeight / 10,
    });

    const dispatch = useDispatch();
    const location = useLocation();
    const isTeacher = useSelector(selectIsTeacher);
    const fullPageMenuIsOpen = useSelector(selectIsMenuOpen);
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const canAccessMemberZone = useSelector(selectCanAccessMemberZone);

    const user = useSelector(selectUser);
    const isActiveMember = user?.features?.includes(ProductFeature.CORE_MEMBERSHIP) || false;

    const memberZoneNavBar = isLoggedIn && canAccessMemberZone;

    const query = useQueryParams();

    const { includeMenu = true } = props;
    const displayMenu =
        includeMenu && !query.hideNav && !skipMenu(location.pathname, isActiveMember);
    const logoTarget = isLoggedIn ? routes.memberHomePage() : routes.home();

    if (skipTopBar(location.pathname, isActiveMember)) {
        return null;
    }

    const renderAppBar = (): JSX.Element => {
        return (
            <AppBar
                position={hideAppbarInitially(location.pathname) ? "fixed" : "sticky"}
                color="transparent"
                className={classNames(classes.appBar, {
                    [classes.memberZoneAppBar]: memberZoneNavBar,
                    [classes.scrolledAppBar]: hasScrolled,
                    [classes.fullPageMenuOpen]: fullPageMenuIsOpen,
                    [classes.scrolledPageFoldAppBar]:
                        hideAppbarInitially(location.pathname) && !hasScrolledPageFold,
                })}
                elevation={0}
            >
                <Box className={classes.container}>
                    <Toolbar
                        disableGutters={true}
                        className={classes.toolbar}
                        style={{ justifyContent: "space-between" }}
                    >
                        <NavLink
                            color="textPrimary"
                            to={logoTarget}
                            additionalOnClick={() => dispatch(closeMenu())}
                        >
                            <img src={"/images/logo128.png"} className={classes.logo} alt="Logo" />
                        </NavLink>

                        {displayMenu && <TopBarMenu />}
                    </Toolbar>
                </Box>
            </AppBar>
        );
    };

    function HideOnScroll(props: { children: React.ReactElement }) {
        const { children } = props;
        const trigger = useScrollTrigger({
            disableHysteresis: true,
            threshold: window.innerHeight / 10,
        });

        return (
            <Slide appear={true} direction="down" in={trigger} timeout={1250}>
                {children}
            </Slide>
        );
    }

    if (hideAppbarInitially(location.pathname)) {
        return (
            <>
                <HideOnScroll>{renderAppBar()}</HideOnScroll>
            </>
        );
    }

    return <>{renderAppBar()}</>;
};
